/**
 * Content & Terminology Utilities
 * 
 * This file contains standardized content patterns and terminology
 * to ensure consistent messaging across the application.
 */

// Standard terminology definitions
export const terminology = {
  // Authentication & Access
  auth: {
    signIn: 'Sign In',
    signUp: 'Sign Up', 
    signOut: 'Sign Out',
    forgotPassword: 'Forgot password?',
    resetPassword: 'Reset Password',
    email: 'Email',
    password: 'Password',
  },
  
  // User Roles
  roles: {
    admin: 'Admin',
    administrator: 'Administrator',
    parent: 'Parent',
    guardian: 'Guardian',
    student: 'Student',
    staff: 'Staff',
    staffMember: 'Staff Member',
    superAdmin: 'Super Admin',
  },
  
  // Business Domain
  domain: {
    class: 'Class',
    classes: 'Classes',
    session: 'Session',
    sessions: 'Sessions',
    family: 'Family',
    families: 'Families',
    enrollment: 'Enrollment',
    enrollments: 'Enrollments',
    schedule: 'Schedule',
    organization: 'Organization',
    location: 'Location',
  },
  
  // Status States
  status: {
    // Student Status
    studentActive: 'Active',
    studentWaitlisted: 'Waitlisted',
    studentWithdrawn: 'Withdrawn',
    studentCompleted: 'Completed',
    studentInactive: 'Inactive',
    
    // Invite Status
    inviteSent: 'Sent',
    inviteAccepted: 'Accepted',
    inviteExpired: 'Expired',
    inviteNotSent: 'Not Sent',
    
    // Family Status
    familyComplete: 'Complete',
    familyIncomplete: 'Incomplete',
    familySetupRequired: 'Setup Required',
    familyInactive: 'Inactive',
    
    // General Status
    active: 'Active',
    inactive: 'Inactive',
    enabled: 'Enabled',
    disabled: 'Disabled',
  },
} as const;

// Action verb standards
export const actions = {
  // Creation actions
  create: 'Create',
  add: 'Add',
  register: 'Register',
  
  // Modification actions
  edit: 'Edit',
  update: 'Update',
  save: 'Save',
  saveChanges: 'Save Changes',
  
  // Deletion actions
  delete: 'Delete',
  remove: 'Remove',
  
  // Navigation actions
  view: 'View',
  close: 'Close',
  cancel: 'Cancel',
  back: 'Back',
  skip: 'Skip',
  
  // Submission actions
  submit: 'Submit',
  send: 'Send',
  
  // Information actions
  learnMore: 'Learn More',
  viewDetails: 'View Details',
} as const;

// Loading state patterns
export const loadingStates = {
  // Authentication
  signingIn: 'Signing in...',
  signingUp: 'Creating account...',
  signingOut: 'Signing out...',
  sendingResetLink: 'Sending reset link...',
  resettingPassword: 'Updating password...',
  
  // CRUD operations
  creating: (entity: string) => `Creating ${entity.toLowerCase()}...`,
  updating: (entity: string) => `Updating ${entity.toLowerCase()}...`,
  deleting: (entity: string) => `Deleting ${entity.toLowerCase()}...`,
  saving: 'Saving changes...',
  loading: 'Loading...',
  processing: 'Processing...',
  
  // Specific actions
  sendingInvite: 'Sending invitation...',
  uploadingFile: 'Uploading file...',
  generatingReport: 'Generating report...',
} as const;

// Success message patterns
export const successMessages = {
  created: (entity: string) => `${entity} created successfully`,
  updated: (entity: string) => `${entity} updated successfully`,
  deleted: (entity: string) => `${entity} deleted successfully`,
  saved: 'Changes saved successfully',
  sent: (item: string) => `${item} sent successfully`,
  uploaded: 'File uploaded successfully',
  invited: 'Invitation sent successfully',
  enrolled: 'Student enrolled successfully',
} as const;

// Error message patterns
export const errorMessages = {
  failed: (action: string, entity?: string) => 
    entity ? `Failed to ${action.toLowerCase()} ${entity.toLowerCase()}` : `Failed to ${action.toLowerCase()}`,
  
  required: (field: string) => `${field} is required`,
  invalid: (field: string) => `${field} is invalid`,
  invalidEmail: 'Please enter a valid email address',
  passwordTooShort: (minLength: number) => `Password must be at least ${minLength} characters`,
  passwordMismatch: 'Passwords do not match',
  
  // Network/Server errors
  networkError: 'Unable to connect. Please check your internet connection.',
  serverError: 'Something went wrong. Please try again later.',
  unauthorized: 'You do not have permission to perform this action.',
  notFound: (entity: string) => `${entity} not found`,
  
  // Validation errors
  emailExists: 'An account with this email already exists',
  phoneInvalid: 'Please enter a valid phone number',
  dateInvalid: 'Please enter a valid date',
  
  // Generic fallback
  generic: 'An error occurred. Please try again.',
} as const;

// Placeholder text patterns
export const placeholders = {
  email: 'your.email@example.com',
  phone: '(555) 123-4567',
  firstName: 'Enter first name',
  lastName: 'Enter last name',
  preferredName: 'Enter preferred name',
  date: 'MM/DD/YYYY',
  search: 'Search by name or email...',
  notes: 'Enter any additional notes...',
  address: '123 Main Street',
  city: 'City',
  state: 'State',
  zipCode: 'ZIP Code',
  classSearch: 'Search classes...',
  studentSearch: 'Search students...',
  familySearch: 'Search families...',
} as const;

// Page title patterns
export const pageTitles = {
  list: (entity: string) => entity, // "Students"
  detail: (entity: string) => `${entity} Details`, // "Student Details"
  create: (entity: string) => `Add ${entity}`, // "Add Student"
  edit: (entity: string) => `Edit ${entity}`, // "Edit Student"
  
  // Specific pages
  dashboard: 'Dashboard',
  schedule: 'Schedule',
  settings: 'Settings',
  reports: 'Reports',
  profile: 'Profile',
  account: 'Account',
} as const;

// Navigation labels
export const navigation = {
  admin: {
    dashboard: 'Dashboard',
    students: 'Students',
    families: 'Families',
    classes: 'Classes',
    schedule: 'Schedule',
    staff: 'Staff',
    settings: 'Settings',
    reports: 'Reports',
    billing: 'Billing',
    notifications: 'Notifications',
  },
  
  parent: {
    dashboard: 'Dashboard',
    myStudents: 'My Students',
    classes: 'Classes',
    schedule: 'Schedule',
    messages: 'Messages',
    account: 'Account',
    billing: 'Billing',
  },
  
  staff: {
    dashboard: 'Dashboard',
    students: 'Students',
    classes: 'My Classes',
    schedule: 'Schedule',
    checkin: 'Check-In',
    profile: 'Profile',
  },
} as const;

// Content helper functions
export const contentUtils = {
  /**
   * Generate consistent button text for entity actions
   */
  getButtonText: (action: string, entity?: string, loading = false): string => {
    if (loading) {
      switch (action.toLowerCase()) {
        case 'create':
        case 'add':
          return entity ? loadingStates.creating(entity) : loadingStates.processing;
        case 'update':
        case 'edit':
          return entity ? loadingStates.updating(entity) : loadingStates.saving;
        case 'delete':
        case 'remove':
          return entity ? loadingStates.deleting(entity) : loadingStates.processing;
        default:
          return loadingStates.processing;
      }
    }
    
    const actionText = actions[action.toLowerCase() as keyof typeof actions] || action;
    return entity ? `${actionText} ${entity}` : actionText;
  },

  /**
   * Generate consistent success messages
   */
  getSuccessMessage: (action: string, entity?: string): string => {
    switch (action.toLowerCase()) {
      case 'create':
      case 'add':
        return entity ? successMessages.created(entity) : 'Created successfully';
      case 'update':
      case 'edit':
        return entity ? successMessages.updated(entity) : successMessages.saved;
      case 'delete':
      case 'remove':
        return entity ? successMessages.deleted(entity) : 'Deleted successfully';
      case 'send':
        return entity ? successMessages.sent(entity) : 'Sent successfully';
      default:
        return successMessages.saved;
    }
  },

  /**
   * Generate consistent error messages
   */
  getErrorMessage: (action: string, entity?: string): string => {
    return errorMessages.failed(action, entity);
  },

  /**
   * Generate consistent page titles
   */
  getPageTitle: (type: string, entity: string): string => {
    switch (type.toLowerCase()) {
      case 'list':
        return pageTitles.list(entity);
      case 'detail':
      case 'view':
        return pageTitles.detail(entity);
      case 'create':
      case 'add':
        return pageTitles.create(entity);
      case 'edit':
      case 'update':
        return pageTitles.edit(entity);
      default:
        return entity;
    }
  },

  /**
   * Validate terminology usage
   */
  validateTerminology: (text: string): { valid: boolean; suggestions: string[] } => {
    const suggestions: string[] = [];
    const lowerText = text.toLowerCase();
    
    // Check for common inconsistencies
    if (lowerText.includes('login')) {
      suggestions.push(`Use "${terminology.auth.signIn}" instead of "Login"`);
    }
    
    if (lowerText.includes('course') && !lowerText.includes('discourse')) {
      suggestions.push(`Use "${terminology.domain.class}" instead of "Course"`);
    }
    
    if (lowerText.includes('super admin') && !text.includes('Super Admin')) {
      suggestions.push(`Use "${terminology.roles.superAdmin}" (title case)`);
    }
    
    if (lowerText.includes('email address')) {
      suggestions.push(`Use "${terminology.auth.email}" for simplicity`);
    }
    
    return {
      valid: suggestions.length === 0,
      suggestions,
    };
  },

  /**
   * Format status display text
   */
  formatStatus: (status: string, context: 'student' | 'invite' | 'family' | 'general' = 'general'): string => {
    const statusKey = `${context}${status.charAt(0).toUpperCase() + status.slice(1)}` as keyof typeof terminology.status;
    return terminology.status[statusKey] || status;
  },

  /**
   * Generate confirmation dialog content
   */
  getConfirmationContent: (action: string, entity: string, entityName?: string) => ({
    title: `${action} ${entity}?`,
    content: `Are you sure you want to ${action.toLowerCase()} ${entityName ? `${entityName}` : `this ${entity.toLowerCase()}`}? This action cannot be undone.`,
    confirmText: `${action} ${entity}`,
    cancelText: actions.cancel,
  }),
};

// Form field validation messages
export const validationMessages = {
  required: (field: string) => `${field} is required`,
  minLength: (field: string, min: number) => `${field} must be at least ${min} characters`,
  maxLength: (field: string, max: number) => `${field} must not exceed ${max} characters`,
  email: 'Please enter a valid email address',
  phone: 'Please enter a valid phone number',
  url: 'Please enter a valid URL',
  number: 'Please enter a valid number',
  date: 'Please enter a valid date',
  password: {
    minLength: (min: number) => `Password must be at least ${min} characters`,
    uppercase: 'Password must contain at least one uppercase letter',
    lowercase: 'Password must contain at least one lowercase letter',
    number: 'Password must contain at least one number',
    special: 'Password must contain at least one special character',
    mismatch: 'Passwords do not match',
  },
} as const;

// Export types for TypeScript support
export type TerminologyKey = keyof typeof terminology;
export type ActionKey = keyof typeof actions;
export type NavigationContext = keyof typeof navigation;
export type ValidationContext = keyof typeof validationMessages;